import React, { FC, useState, useMemo, useRef, useEffect } from "react";
import { MDBInput } from "mdb-react-ui-kit";
import { useRouter } from "next/router";
import { useQuery } from "@tanstack/react-query";
import generalSvg from "@/utils/svg-icon/general";
import contactsSvg from "@/utils/svg-icon/contacts";
import { TagDestination } from "@/models/common/common";
import { ToursByDestinationEntity } from "@/models/home";
import useNavBarData from "@/utils/queries/hooks/useNavBarData";
import { getFiltersData } from "@/utils/queries";
import styles from "../../../styles/home/Home.HeroSearchBar.module.scss";

interface SearchResult {
    type: "tag" | "destination";
    title: string;
    tagDestination?: string;
    city?: string;
}

interface HeroSearchBarProps {
    onFocusChange?: (isFocused: boolean) => void;
    disableActiveAnimation?: boolean;
}

const HeroSearchBar: FC<HeroSearchBarProps> = ({
    onFocusChange,
    disableActiveAnimation = false,
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [showResults, setShowResults] = useState(false);
    const { SearchLine } = generalSvg;
    const { LocationLine } = contactsSvg;
    const { data: navBarData } = useNavBarData();
    const { data: filtersData } = useQuery({
        queryKey: ["filters-data"],
        queryFn: getFiltersData,
        refetchOnWindowFocus: false,
    });
    const router = useRouter();
    const containerRef = useRef<HTMLDivElement>(null);
    const resultsRef = useRef<HTMLDivElement>(null);

    // Helper function per cercare nei tag
    const searchInTags = (
        query: string,
        results: SearchResult[],
        addedDestinations: Set<string>
    ): void => {
        if (!navBarData?.tagDestinations) return;

        navBarData.tagDestinations.forEach((tag: TagDestination) => {
            const tagTitle = tag.title?.toLowerCase() || "";
            const tagDestination = tag.tagDestination?.toLowerCase() || "";

            if (tagTitle.includes(query) || tagDestination.includes(query)) {
                results.push({
                    type: "tag",
                    title: tag.title || tag.tagDestination || "",
                    tagDestination: tag.tagDestination,
                });
                if (tag.tagDestination) {
                    addedDestinations.add(tag.tagDestination.toLowerCase());
                }
            }
        });
    };

    // Helper function per cercare nelle destinazioni dalla navbar
    const searchInNavBarDestinations = (
        query: string,
        results: SearchResult[],
        addedDestinations: Set<string>
    ): void => {
        if (!navBarData?.toursByDestination) return;

        navBarData.toursByDestination.forEach(
            (destination: ToursByDestinationEntity) => {
                const city = destination.city?.toLowerCase() || "";
                const title = destination.title?.toLowerCase() || "";

                if (
                    (city.includes(query) || title.includes(query)) &&
                    !addedDestinations.has(city)
                ) {
                    results.push({
                        type: "destination",
                        title: destination.title || destination.city || "",
                        city: destination.city,
                    });
                    if (destination.city) {
                        addedDestinations.add(city);
                    }
                }
            }
        );
    };

    // Helper function per cercare nelle destinazioni dai filtri
    const searchInFilterDestinations = (
        query: string,
        results: SearchResult[],
        addedDestinations: Set<string>
    ): void => {
        if (!filtersData?.destinations) return;

        filtersData.destinations.forEach((destination: string) => {
            const destinationLower = destination.toLowerCase();

            if (
                destinationLower.includes(query) &&
                !addedDestinations.has(destinationLower)
            ) {
                results.push({
                    type: "destination",
                    title: destination,
                    city: destination,
                });
                addedDestinations.add(destinationLower);
            }
        });
    };

    // Cerca tra tag e destinazioni
    const searchResults = useMemo((): SearchResult[] => {
        if (!searchValue.trim() || (!navBarData && !filtersData)) {
            return [];
        }

        const query = searchValue.toLowerCase().trim();
        const results: SearchResult[] = [];
        const addedDestinations = new Set<string>();

        searchInTags(query, results, addedDestinations);
        searchInNavBarDestinations(query, results, addedDestinations);
        searchInFilterDestinations(query, results, addedDestinations);

        // Su desktop mostra tutti i risultati (saranno limitati visivamente con max-height e scroll)
        // Su mobile mostra max 10 risultati
        return results;
    }, [searchValue, navBarData, filtersData]);

    // Gestisci il click fuori dal componente per chiudere i risultati
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setShowResults(false);
            }
        };

        if (showResults) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showResults]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setSearchValue(value);
        setShowResults(value.trim().length > 0);
    };

    const handleInputFocus = () => {
        setIsFocused(true);
        onFocusChange?.(true);
        if (searchValue.trim().length > 0) {
            setShowResults(true);
        }
    };

    const handleInputBlur = () => {
        setIsFocused(false);
        onFocusChange?.(false);
        // Delay per permettere il click sui risultati
        setTimeout(() => {
            setShowResults(false);
        }, 200);
    };

    const handleResultClick = (result: SearchResult) => {
        const query: Record<string, string> = {};

        if (result.type === "tag" && result.tagDestination) {
            query.tagDestination = result.tagDestination;
        } else if (result.type === "destination" && result.city) {
            query.destination = result.city;
        }

        router.push({
            pathname: "/tours-destinations",
            query,
        });
    };

    const handleSearchClick = () => {
        router.push("/tours-destinations");
    };

    return (
        <div className={styles.heroSearchContainer} ref={containerRef}>
            <div
                className={`${styles.searchInputWrapper} ${
                    isFocused && !disableActiveAnimation
                        ? styles.searchInputWrapperActive
                        : ""
                } ${
                    isFocused && disableActiveAnimation
                        ? styles.searchInputWrapperActiveModal
                        : ""
                }`}
            >
                <div className={styles.searchIconLeft}>
                    <LocationLine alt="location-arrow" />
                </div>
                <div className={styles.searchInputContent}>
                    {!searchValue && (
                        <div className={styles.placeholderContent}>
                            {!isFocused ? (
                                <>
                                    <div className={styles.placeholderLabel}>
                                        Destinations
                                    </div>
                                    <div className={styles.placeholderText}>
                                        Where are you going?
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div
                                        className={styles.placeholderTextActive}
                                    >
                                        Where are you going?
                                    </div>
                                    <div
                                        className={styles.placeholderUnderline}
                                    />
                                </>
                            )}
                        </div>
                    )}
                    <MDBInput
                        id="hero-search-input"
                        type="text"
                        className={styles.searchInput}
                        placeholder=""
                        value={searchValue}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        wrapperClass={styles.inputWrapper}
                    />
                    {showResults && searchResults.length > 0 && (
                        <div
                            className={styles.searchResults}
                            ref={resultsRef}
                            onMouseDown={e => e.preventDefault()}
                        >
                            {searchResults.map(result => {
                                const uniqueKey =
                                    result.type === "tag"
                                        ? `tag-${
                                              result.tagDestination ||
                                              result.title
                                          }`
                                        : `destination-${
                                              result.city || result.title
                                          }`;
                                return (
                                    <div
                                        key={uniqueKey}
                                        className={styles.searchResultItem}
                                        onClick={() =>
                                            handleResultClick(result)
                                        }
                                    >
                                        <div className={styles.resultIcon}>
                                            <LocationLine alt="location" />
                                        </div>
                                        <div className={styles.resultContent}>
                                            <div className={styles.resultTitle}>
                                                {result.title}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    className={styles.searchButton}
                    aria-label="Search"
                    onClick={handleSearchClick}
                >
                    <SearchLine alt="search" />
                </button>
            </div>
        </div>
    );
};

export default HeroSearchBar;
